/**
 * Die Liste der setzbaren Status -- auf der Artikel- und der Packstück-Seite
 * und in der Mehrfachauswahl.
 *
 * Nur aktive Status, und nicht der, den der Artikel schon hat (ADR 0005).
 *
 * Seit ADR 0025:
 * - Status mit „Nur Admin“ fehlen für Benutzer ohne Adminrechte. Eine Zeile
 *   sagt, wer sie setzt -- keine Sackgasse (CLAUDE.md §7). Ein Administrator
 *   setzt sie nur mit Netz, die Buchung geht dann sofort hinaus.
 * - Status mit „Mit Bestätigung“ fragen ohne Popup: Nach dem ersten Tipp wird
 *   genau dieser Knopf rot, erst der zweite Tipp setzt. Nach fünf Sekunden
 *   oder mit einem Tipp woanders ist er wieder wie vorher.
 */

import { useEffect, useRef, useState } from "react";
import type { StatusArt } from "@thw/contracts";
import { sitzungLesen } from "../api/klient.js";
import { useOnline } from "../api/online.js";
import { useStatusArten } from "../lokal/bestand.js";
import { FRAGE_MS, frageText, tippen, type Frage } from "../lokal/bestaetigung.js";
import { Farbpunkt } from "./bausteine.jsx";

/** Eine lokale Kopie von vor Vertrag 2.10 kennt die Haken nicht -- dann sind sie aus. */
const nurAdmin = (a: StatusArt): boolean => (a.nur_admin as boolean | undefined) === true;
const mitBestaetigung = (a: StatusArt): boolean =>
  (a.mit_bestaetigung as boolean | undefined) === true;

/** „Verloren, Gestohlen und Archiviert“. */
function aufzaehlen(namen: readonly string[]): string {
  if (namen.length <= 1) return namen.join("");
  return `${namen.slice(0, -1).join(", ")} und ${namen.at(-1) ?? ""}`;
}

export function StatusWahl({
  aktuell,
  aufWahl,
  anzahl,
}: {
  aktuell: string;
  aufWahl: (schluessel: string, bezeichnung: string) => void;
  /** In der Mehrfachauswahl: für wie viele Geräte -- steht dann in der Frage. */
  anzahl?: number;
}) {
  const arten = useStatusArten().filter((a) => a.aktiv && a.schluessel !== aktuell);
  const admin = sitzungLesen()?.benutzer.rolle === "administrator";
  const online = useOnline();
  const [frage, setFrage] = useState<Frage | null>(null);
  const liste = useRef<HTMLUListElement>(null);

  // Die Frage verfällt nach einer Weile und mit jedem Tipp außerhalb der Liste.
  useEffect(() => {
    if (frage === null) return;
    const uhr = window.setTimeout(() => {
      setFrage(null);
    }, FRAGE_MS);
    const woanders = (e: PointerEvent): void => {
      if (!(e.target instanceof Node) || liste.current?.contains(e.target) !== true) {
        setFrage(null);
      }
    };
    document.addEventListener("pointerdown", woanders);
    return () => {
      window.clearTimeout(uhr);
      document.removeEventListener("pointerdown", woanders);
    };
  }, [frage]);

  const setzbar = arten.filter((a) => admin || !nurAdmin(a));
  const nurFuerAdmins = admin ? [] : arten.filter(nurAdmin).map((a) => a.bezeichnung);

  const getippt = (s: StatusArt): void => {
    const schritt = tippen(frage, s.schluessel, mitBestaetigung(s), Date.now());
    if (schritt.tun === "warten") return;
    if (schritt.tun === "fragen") {
      setFrage(schritt.frage);
      return;
    }
    setFrage(null);
    aufWahl(s.schluessel, s.bezeichnung);
  };

  return (
    <ul ref={liste} className="mt-3 flex flex-col gap-1">
      {setzbar.map((s) => {
        const fragt = frage?.schluessel === s.schluessel;
        const ohneNetz = nurAdmin(s) && !online;
        return (
          <li key={s.schluessel}>
            <button
              type="button"
              disabled={ohneNetz}
              className={`flex min-h-12 w-full items-center gap-3 rounded-xl px-4 text-left font-semibold disabled:opacity-50 ${
                fragt
                  ? "justify-center bg-red-700 text-white active:bg-red-800"
                  : "bg-neutral-100 active:bg-neutral-200 dark:bg-neutral-700"
              }`}
              onClick={() => {
                getippt(s);
              }}
            >
              {fragt ? (
                frageText(s.bezeichnung, anzahl)
              ) : (
                <>
                  <Farbpunkt farbe={s.farbe} gross />
                  {s.bezeichnung}
                  {ohneNetz && <span className="ml-auto text-sm font-normal">nur mit Netz</span>}
                </>
              )}
            </button>
          </li>
        );
      })}
      {frage !== null && (
        <li className="px-1 text-sm text-neutral-600 dark:text-neutral-300" aria-live="polite">
          Noch einmal tippen zum Bestätigen.
        </li>
      )}
      {nurFuerAdmins.length > 0 && (
        <li className="px-1 pt-1 text-sm text-neutral-600 dark:text-neutral-300">
          {aufzaehlen(nurFuerAdmins)} setzt ein Administrator.
        </li>
      )}
    </ul>
  );
}
